import { useState, useEffect } from 'react'

export interface User {
  id: string
  email: string
  name: string
  role: 'demo' | 'company_user' | 'company_admin' | 'super_admin'
  companyId: string | null
  emailVerified: boolean
  createdAt: string
  lastLogin: string | null
}

export interface UsersResponse {
  users: User[]
}

export interface UseUsersResult {
  users: User[]
  loading: boolean
  error: string | null
  refetchUsers: () => void
}

export function useUsers(companyId?: string): UseUsersResult {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const API_BASE_URL = 'http://localhost:3000'

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Build URL with company_id query param for super admins
      let url = `${API_BASE_URL}/api/company/users`
      const params = new URLSearchParams()

      // If companyId is provided, add it as query param (for super admins viewing specific company)
      if (companyId) {
        params.append('company_id', companyId)
      }

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to fetch users (${response.status})`)
      }

      const data: UsersResponse = await response.json()
      setUsers(data.users)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch users'
      setError(errorMessage)
      console.error('Error fetching users:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [companyId]) // Refetch when companyId changes

  return {
    users,
    loading,
    error,
    refetchUsers: fetchUsers
  }
}
